import Backbone from "backbone";
import _ from "underscore";
import Translator from "../../utils/translator";
import { empty } from "../../utils/vanilla";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { uuid } from "geotab-uuid";
import itemTemplate from "./checklist.tmpl";
import defaultLabelTemplate from "./checklist.defaultLabel.tmpl";

interface IListItemOptions {
    labelTemplate?: string;
    isCheckable: boolean;
    selectCallback?: (item: Item, event) => void;
    beforeSelectCallback?: (item: Item, event) => boolean;
    context: any;
    translator: Translator;
    noTranslate?: boolean;
    el?: any;
    reverseItems?: boolean;
    customParams?: any;
}

interface IChecklistOptions extends IListItemOptions {
    items: any[] | Backbone.Collection<Backbone.Model>;
    labelledBy?: string;
}

interface IListViewOptions extends IListItemOptions, Backbone.ViewOptions<Item> {
    collection: List;
    parentCollection: Backbone.Collection<Backbone.Model>;
    labelledBy?: string;
}

interface IListItemViewOptions extends IListItemOptions, Backbone.ViewOptions<Item> {}

const ITEM_VIEW_CLASS_NAME = "checklist__item";
const ITEM_VIEW_DISABLED_CLASS_NAME = "disabled";
const ITEM_VIEW_ID_REF = "checklist__";
const itemViewTemplate = _.template(itemTemplate);

export class Item extends Backbone.Model {

    parentModel: Backbone.Model;

    static generateItem (this: void, parentItem: Backbone.Model) {
        let item = new Item(parentItem.toJSON());
        item.parentModel = parentItem;
        return item;
    }

    //Default attributes for the item.
    defaults () {
        return {
            isAction: false,
            selected: false,
            isCheckable: true,
            subtitle: "",
            className: "",
            icon: null,
            iconLabel: ""
        };
    }

    // No sync
    sync () {
        return false;
    }

    //Toggle the selected state of this item.
    toggle (selectCallback, context) {
        this.set({ selected: !this.get("selected") });
        selectCallback && selectCallback.call(context, this);
    }

    clear () {
        this.destroy();
    }
}

let defaultLblTemplate;

//The DOM element for an item...
class ItemView {

    el: HTMLElement;
    contentStr: string;
    disabled: boolean;
    private readonly labelTemplate;
    private readonly options: IListItemViewOptions;
    private readonly model: Item;
    private renderPromise: Promise<void>;

    // The ItemView listens for changes to its model, re-rendering. Since there's
    // a one-to-one correspondence between an Item and an ItemView in this
    // app, we set a direct reference on the model for convenience.
    constructor (options: IListItemViewOptions, labelTemplateFunc?: (data) => string) {
        this.options = options;
        this.model = options.model || new Item();
        this.el = options.el || document.createElement(options.tagName || "div");
        this.disabled = this.model.get("disabled");

        this.model.on("change", this.onModelChanged);
        this.model.on("destroy", this.onModelRemoved);
        this.model.on("remove", this.onModelRemoved);
        this.model.parentModel.on("change", this.onParentModelChanged);
        this.model.parentModel.on("destroy", this.onParentModelRemoved);
        this.model.parentModel.on("remove", this.onParentModelRemoved);

        if (!labelTemplateFunc) {
            defaultLblTemplate = defaultLblTemplate || _.template(defaultLabelTemplate);
        }

        this.labelTemplate = labelTemplateFunc || defaultLblTemplate;
    }

    // Re-render the titles of the item.
    render () {
        let obj = this.model.toJSON();
        let templateStr;

        obj.isCheckable = this.options.isCheckable;
        this.options.customParams && Object.keys(this.options.customParams).forEach(paramKey => obj[paramKey] = this.options.customParams[paramKey]);
        obj.noTranslate = this.options.noTranslate || false;

        let id = obj.id || uuid();
        obj.labelId = `${ ITEM_VIEW_ID_REF }${ id }-label`.trim();
        obj.labelTemplate = this.labelTemplate(obj);

        this.el.className = `${ ITEM_VIEW_CLASS_NAME } ${ this.disabled ? ITEM_VIEW_DISABLED_CLASS_NAME : "" } ${ this.model.get("className") || "" }`.trim();
        this.el.id = `${ ITEM_VIEW_ID_REF }${ id }`.trim();

        if (obj.isCheckable) {
            this.el.setAttribute("role", "option");
            this.el.setAttribute("aria-selected", obj.selected ? "true" : "false");
            this.el.setAttribute("tabindex", "0");
        }

        templateStr = itemViewTemplate(obj);

        this.renderPromise && this.renderPromise.cancel();

        if (this.options.translator.language === "en" || obj.noTranslate) {
            // Don't attempt to translate elements if we're already English.
            this.el.innerHTML = templateStr;
        } else {
            this.renderPromise = this.options.translator.translateTemplate(templateStr)
            .then(tmp => {
                empty(this.el);

                let tempEl = document.createDocumentFragment();
                [].forEach.call(tmp, item => {
                    tempEl.appendChild(item);
                });
                this.el.appendChild(tempEl);
            }, () => {
                // If we get lost in translation, just render *something*
                this.el.innerHTML = templateStr;
            });
        }

        this.options.selectCallback && this.el.addEventListener("click", this.toggleDone, false);
        this.options.selectCallback && this.el.addEventListener("keyup", this.toggleDone, false);

        return this;
    }

    remove () {
        this.el.parentNode && this.el.parentNode.removeChild(this.el);

        this.model.off("change", this.onModelChanged);
        this.model.off("destroy", this.onModelRemoved);
        this.model.off("remove", this.onModelRemoved);
        this.model.parentModel.off("change", this.onParentModelChanged);
        this.model.parentModel.off("destroy", this.onParentModelRemoved);
        this.model.parentModel.off("remove", this.onParentModelRemoved);
        this.options.selectCallback && this.el.removeEventListener("click", this.toggleDone, false);
        this.options.selectCallback && this.el.removeEventListener("keyup", this.toggleDone, false);
    }

    private readonly onModelChanged = () => this.render();
    private readonly onModelRemoved = () => this.remove();
    private readonly onParentModelChanged = parentModel => this.model.set(Item.generateItem(parentModel).toJSON());
    private readonly onParentModelRemoved = () => this.model.destroy();

    // Toggle the `"done"` state of the model.
    private readonly toggleDone = event => {
        if (event.type === "keyup" && ["Enter", " "].indexOf(event.key) < 0) {
            return undefined;
        }
        if (this.options.beforeSelectCallback && !this.options.beforeSelectCallback(this.model, event)) {
            return false;
        }

        if (!this.disabled) {
            dispatcher.trigger(DispatcherEvents.DriveAppInteraction, event);
            this.options.isCheckable && this.model.set("selected", !this.model.get("selected"));
            this.options.selectCallback && this.options.selectCallback(this.model, event);
        }
        return undefined;
    };
}

export class List extends Backbone.Collection<Item> {

    static model = Item;

    //Filter the list of all items that are selected
    selected () {
        return this.filter(item => item.get("selected"));
    }

    //Filter down the list to only items that are still not finished.
    unselected () {
        return this.without.apply(this, this.selected());
    }

    setAll (selected) {
        this.each(item => {
            item.set({ selected });
        });
    }
}

export class ListView extends Backbone.NativeView<Item> {

    collection: List;
    private readonly options: IListViewOptions;
    private subViews: any[];
    private readonly labelTemplateFunc;

    constructor (options: IListViewOptions) {
        super(options);
        this.collection = options.collection;
        this.options = options;
        this.el.classList.add("checklist");
        this.labelTemplateFunc = this.options.labelTemplate && _.template(this.options.labelTemplate);

        this.listenTo(this.collection, "add", (model: Item) => {
            this.addOne(model, this.options.parentCollection.indexOf(model.parentModel));
        });

        this.listenTo(this.collection, "reset", () => {
            this.subViews = [];
            this.addAll();
            this.render();
        });

        this.listenTo(this.collection, "remove", (_1, _2, params) => {
            this.removeOne(params.index);
        });

        this.listenTo(this.options.parentCollection, "add", parentModel => {
            this.collection.add(Item.generateItem(parentModel));
        });

        this.listenTo(this.options.parentCollection, "reset", parentCollection => this.collection.reset(parentCollection.models.map(Item.generateItem)));

        this.subViews = [];
        this.addAll();
    }

    render () {
        empty(this.el);

        if (this.collection.length) {

            let container = document.createDocumentFragment();

            for (let i = 0; i < this.subViews.length; i++) {
                container.appendChild(this.subViews[i].render().el);
            }

            this.el.appendChild(container);
            this.el.setAttribute("role", this.options.isCheckable ? "listbox" : "list");
            if (this.options.isCheckable) {
                this.el.setAttribute("aria-multiselectable", "true");
                this.el.setAttribute("tabindex", "0");
            }
            this.options.labelledBy && this.el.setAttribute("aria-labelledby", this.options.labelledBy);
        }
        return this;
    }

    addOne (item, position) {
        let itemView = this.createItemView(item);
        let previousView = this.subViews[position - 1];
        let parentNode = previousView ? previousView.el.parentNode : this.el;
        let nextElement = previousView ? previousView.el.nextSibling : this.el.firstChild;

        this.subViews.splice(position, 0, itemView);
        parentNode && parentNode.insertBefore(itemView.render().el, nextElement);
    }

    // Add all items in the **Todos** collection at once.
    addAll () {
        this.collection.each(item => this.subViews.push(this.createItemView(item)));
    }

    removeOne (position) {
        this.subViews.splice(position, 1);
    }

    // Remove this view
    remove () {
        // clean up events
        let model = this.collection.first();
        while (model) {
            model.destroy();
            model = this.collection.first();
        }

        this.subViews = [];
        this.collection.reset();

        //Maintain the el in the DOM
        this.el.parentNode && this.el.parentNode.removeChild(this.el);
        this.stopListening();
        super.remove();
        return this;
    }

    private createItemView (item: Item) {
        let hasNoTranslateOverride = item.has("noTranslate");

        return new ItemView({
            tagName: "li",
            model: item,
            selectCallback: this.options.selectCallback,
            beforeSelectCallback: this.options.beforeSelectCallback,
            context: this.options.context,
            isCheckable: this.options.isCheckable,
            translator: this.options.translator,
            noTranslate: hasNoTranslateOverride ? item.get("noTranslate") : (this.options.noTranslate || false)
        }, this.labelTemplateFunc);
    }
}

export function generateChecklist(options: IChecklistOptions): ListView {
    let getReversedItems = (models: Backbone.Model[]) => {
        let items: Item[] = [];
        for (let i = models.length - 1; i >= 0; i--) {
            items.push(Item.generateItem(models[i]));
        }
        return new List(items);
    };

    let data = options.items;
    let container = options.el && (options.el[0] || options.el);
    let isCollection: boolean = data instanceof Backbone.Collection;
    let generateItems = (items: any[]) => items.map(item => new Backbone.Model(item.toJSON ? item.toJSON() : item));
    let parentCollection: Backbone.Collection<Backbone.Model> = isCollection
        ? data as Backbone.Collection<Backbone.Model>
        : new Backbone.Collection(generateItems(<any[]>data));
    let list = options.reverseItems ? getReversedItems(parentCollection.models) : new List(parentCollection.models.map(item => Item.generateItem(item)));
    let listViewParams: IListViewOptions = {
        el: document.createElement("ul"),
        collection: list,
        parentCollection,
        labelledBy: options.labelledBy,
        labelTemplate: options.labelTemplate,
        isCheckable: options.isCheckable,
        beforeSelectCallback: options.beforeSelectCallback,
        selectCallback: options.selectCallback,
        context: options.context,
        translator: options.translator,
        noTranslate: options.noTranslate || false
    };

    container && container.appendChild(listViewParams.el);
    let listView = new ListView(listViewParams);
    listView.render();
    return listView;
}